import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import useClickOutside from "../../../../hooks/useClickOutside";
import useMenuDropUp from "../../../../hooks/useMenuDropUp";
import ExerciseInsertMenuItems from "../../../../parts/menus/ExerciseInsertMenuItems";
import SvgIcon from "../../../../parts/SvgIcon";
import { useRillContext } from "../../../../rill/RillReact/share/base";

const PbExerciseOptions = ({ activeExercise, week, session }) => {
  const {
    locals: { query },
  } = useRillContext();
  const { t } = useTranslation(["athlete-portal", "common"]);
  const [opened, setOpened] = useState(false);
  const detailsRef = useRef(null);
  // Blur alone doesn't close the menu when another menu's button is clicked
  // (browsers that don't focus the button on click, e.g. Safari), so two
  // menus could stay open. A click anywhere outside closes this one.
  useClickOutside(detailsRef, () => setOpened(false), opened);
  const dropUp = useMenuDropUp(detailsRef, opened);

  return (
    <div className="more">
      <details ref={detailsRef} role="list" open={opened}>
        <summary
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpened(!opened);
          }}
          onBlur={(e) => {
            if (e.relatedTarget?.role === "listbox") return;
            e.preventDefault();
            setOpened(false);
          }}
          aria-haspopup="listbox"
          className="p-0! h-auto!"
        >
          <SvgIcon src="/img/new-icons/vuesax-linear-add-square.svg" />
        </summary>
        <ul
          role="listbox"
          className={`exercise-menu${opened ? " opened" : ""}${dropUp ? " drop-up" : ""}`}
          tabIndex="-1"
        >
          <ExerciseInsertMenuItems
            activeExercise={activeExercise}
            week={week}
            session={session}
            onSelect={() => setOpened(false)}
          />
          <li
            onClick={(e) => {
              e.stopPropagation();
              setOpened(false);
              submit("/actions/refresh?", {
                method: "GET",
                body: {
                  "modal[type]": "progression",
                  exerciseId: activeExercise._id,
                  type: "exercise",
                },
              });
            }}
          >
            <a className="insert-menu-item">
              <SvgIcon src="/img/new-icons/plus-minus.svg" useCurrentColor />
              {t("programs.progression")}
            </a>
          </li>
          {!activeExercise.complex && activeExercise.primary !== "metcon" && (
            <li
              onClick={(e) => {
                e.stopPropagation();
                setOpened(false);
                submit("/actions/refresh?", {
                  method: "GET",
                  body: {
                    "modal[type]": "swap-exercise",
                    exercise: activeExercise._id,
                    editMode: true,
                  },
                });
              }}
            >
              <a>Swap</a>
            </li>
          )}
          {!activeExercise.complex && (
            <li
              onClick={(e) => {
                e.stopPropagation();
                setOpened(false);
                submit("/actions/refresh?", {
                  method: "GET",
                  body: {
                    "modal[type]": "duplicate-workout",
                    week,
                    session,
                    isExercise: true,
                    exerciseId: activeExercise._id,
                  },
                });
              }}
            >
              <a>{t("programs.duplicate")}</a>
            </li>
          )}
          <li
            onClick={(e) => {
              e.stopPropagation();
              setOpened(false);
              const body = {
                "modal[type]": "add-exercise",
                exercise: activeExercise._id,
                editMode: true,
              };
              if (activeExercise.complex && activeExercise.exercises) {
                body.addType = "complex";
                body.complex = activeExercise.complex;
                body.exercises = activeExercise.exercises.map((ex) => ex._id);
              }
              if (activeExercise.primary === "metcon") {
                body.addType = "metcon";
              }
              submit("/actions/refresh?", {
                method: "GET",
                body,
              });
            }}
          >
            <a>{t("common:edit")}</a>
          </li>
          <li role="separator" className="exercise-menu-sep" />
          <li
            className="exercise-menu-danger"
            onClick={(e) => {
              e.stopPropagation();
              setOpened(false);
              if (activeExercise.complex && activeExercise.exercises) {
                submit("/actions/complex/remove", {
                  method: "POST",
                  body: {
                    exercises: activeExercise.exercises,
                    cycleId: activeExercise.cycle.id,
                    programOrder: activeExercise.cycle.program,
                    sessionOrder: activeExercise.cycle.programSession,
                  },
                });
              } else {
                submit(`/actions/exercise/remove/${activeExercise._id}`, {
                  method: "GET",
                  body: {
                    proxyAthlete: query.athlete,
                  },
                });
              }
            }}
          >
            <a>{t("programs.deleteExercise")}</a>
          </li>
        </ul>
      </details>
    </div>
  );
};

export default PbExerciseOptions;
